import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Chip } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { filterByWindow } from '../statistics';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';

export default function MultiOverlayWidget({ recordings = [], widgetConfig }) {
  const { currentTime, seek, window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey1 = widgetConfig?.sensorKey1 || '';
  const sensorKey2 = widgetConfig?.sensorKey2 || '';

  const rec = recordings.find((r) => r.id === recId);

  const chartData = useMemo(() => {
    if (!rec?.data || !sensorKey1) return null;
    let points1 = rec.data[sensorKey1] || [];
    let points2 = sensorKey2 ? (rec.data[sensorKey2] || []) : [];

    if (timeWindow) {
      points1 = filterByWindow(points1, timeWindow.start, timeWindow.end);
      points2 = sensorKey2 ? filterByWindow(points2, timeWindow.start, timeWindow.end) : [];
    }

    if (points1.length === 0) return null;

    const series = [
      { type: 'line', name: sensorKey1, data: points1.map((dp) => dp.value) },
    ];
    const labels = points1.map((dp) => dp.time);

    if (sensorKey2 && points2.length > 0) {
      series.push({ type: 'line', name: sensorKey2, data: points2.map((dp) => dp.value) });
    }

    return { series, labels };
  }, [rec, sensorKey1, sensorKey2, timeWindow]);

  const name1 = friendlyNameConverter(sensorKey1 || '');
  const name2 = friendlyNameConverter(sensorKey2 || '');

  const chartOptions = useChart({
    fill: { type: chartData?.series.map(() => 'solid') || ['solid'] },
    labels: chartData?.labels || [],
    xaxis: { type: 'datetime', min: timeWindow?.start, max: timeWindow?.end, labels: { datetimeUTC: false } },
    ...(sensorKey2 && {
      yaxis: [
        { title: { text: name1 } },
        { opposite: true, title: { text: name2 } },
      ],
    }),
    ...(!sensorKey2 && { yaxis: { title: { text: name1 } } }),
    annotations: {
      xaxis: [{
        x: currentTime,
        borderColor: '#775DD0',
        label: { borderColor: '#775DD0', style: { color: '#fff', background: '#775DD0' }, text: 'Now' },
      }],
    },
    chart: {
      toolbar: { show: true, tools: { download: true, zoom: true, pan: true } },
      events: {
        markerClick: (_, __, opts) => {
          if (chartData?.labels?.[opts.dataPointIndex]) seek(chartData.labels[opts.dataPointIndex]);
        },
      },
    },
    legend: { position: 'bottom', formatter: (val) => friendlyNameConverter(val) },
    tooltip: {
      custom: ({ series, dataPointIndex, w }) => {
        const items = series.map((s, i) => {
          const val = s[dataPointIndex] ?? 'N/A';
          const color = w.globals.colors[i];
          const label = friendlyNameConverter(w.globals.seriesNames[i]);
          return `<div style="display:flex;align-items:center"><span style="background:${color};border-radius:50%;width:10px;height:10px;margin-right:6px"></span>${label}: <b>${val}</b></div>`;
        }).join('');
        return `<div style="padding:8px">${items}</div>`;
      },
    },
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {chartData ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}
          <ReactApexChart type="line" series={chartData.series} options={chartOptions} height="90%" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and sensors</Typography>
        </Box>
      )}
    </Box>
  );
}
